import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ExternalLink, Video, Receipt, Dumbbell, Gift, Calendar, ArrowRight } from "lucide-react";
import { projects } from "../../data/mock";

const iconMap = {
  Video,
  Receipt,
  Dumbbell,
  Gift,
};

const ProjectsSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="projects" className="py-24 sm:py-32 relative" ref={ref} aria-label="Featured Projects">
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[rgba(127,74,142,0.03)] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-sm font-semibold text-[rgb(218,255,1)] uppercase tracking-widest">
            Projects
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3 tracking-tight">
            Things I've{" "}
            <span className="text-[rgb(218,255,1)]">built</span>
          </h2>
        </motion.div>

        {/* Project cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => {
            const Icon = iconMap[project.icon];
            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 * index }}
                className="group relative rounded-2xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] hover:border-[rgba(218,255,1,0.4)] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)]"
              >
                {/* Top accent */}
                <div
                  className="h-1 w-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: project.color }}
                />

                <div className="p-6 sm:p-7">
                  {/* Project icon & title */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ background: `${project.color}15` }}
                      >
                        {Icon && (
                          <Icon size={22} style={{ color: project.color }} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-white">
                            {project.link ? (
                              <a
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-[rgb(218,255,1)] transition-colors duration-200"
                              >
                                {project.title}
                              </a>
                            ) : (
                              project.title
                            )}
                          </h3>
                          {project.isLive && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[rgb(34,197,94)] bg-[rgba(34,197,94,0.12)] rounded-full border border-[rgba(34,197,94,0.3)]">
                              <span className="w-1.5 h-1.5 rounded-full bg-[rgb(34,197,94)] animate-pulse" />
                              Live
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[rgb(161,161,170)]">
                          {project.subtitle}
                        </p>
                      </div>
                    </div>
                    {project.link ? (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                        aria-label={`Visit ${project.title}`}
                      >
                        <ExternalLink
                          size={18}
                          className="text-[rgb(63,63,63)] group-hover:text-[rgb(218,255,1)] hover:scale-110 transition-all duration-200"
                        />
                      </a>
                    ) : (
                      <span className="flex-shrink-0 opacity-0 pointer-events-none">
                        <ExternalLink size={18} />
                      </span>
                    )}
                  </div>

                  {/* Duration */}
                  {project.duration && (
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar size={13} className="text-[rgb(161,161,170)]" />
                      <span className="text-xs text-[rgb(161,161,170)]">
                        {project.duration}
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  <p className="text-sm text-[rgb(218,218,218)] leading-relaxed mb-5">
                    {project.description}
                  </p>

                  {/* Highlights */}
                  <ul className="space-y-2 mb-5">
                    {project.highlights.slice(0, 3).map((h, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-xs text-[rgb(161,161,170)]"
                      >
                        <span
                          className="w-1 h-1 rounded-full flex-shrink-0"
                          style={{ background: project.color }}
                        />
                        {h}
                      </li>
                    ))}
                  </ul>

                  {/* Tech stack */}
                  <div className="flex flex-wrap gap-1.5">
                    {project.techStack.map((tech) => (
                      <span
                        key={tech}
                        className="px-2.5 py-1 text-xs font-medium text-[rgb(161,161,170)] bg-[rgb(38,40,42)] rounded-md border border-[rgba(255,255,255,0.05)]"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Live link button */}
                  {project.link && (
                    <div className="mt-5 pt-5 border-t border-[rgba(255,255,255,0.06)]">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn inline-flex items-center gap-2 text-sm font-semibold text-[rgb(218,255,1)] hover:gap-3 transition-all duration-200"
                      >
                        {project.featured ? "View Live" : "Live"}
                        <ArrowRight
                          size={15}
                          className="group-hover/btn:translate-x-0.5 transition-transform duration-200"
                        />
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
